import { useState, useEffect } from 'react';
import { useAuthStore } from '@/store/authStore';
import { checkDuplicateProduct, DuplicateCheckResult } from '../services/productsApi';
import { AlertTriangle, AlertCircle, ExternalLink, ChevronDown, ChevronUp, CopySlash } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

interface DuplicateAlertBannerProps {
    name?: string;
    sku?: string;
    barcode?: string;
    excludeId?: string;
}

export function DuplicateAlertBanner({
    name = '',
    sku = '',
    barcode = '',
    excludeId,
}: DuplicateAlertBannerProps) {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId;

    const [result, setResult] = useState<DuplicateCheckResult | null>(null);
    const [isChecking, setIsChecking] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        // Only check if at least one field has meaningful length
        const hasName = name.trim().length >= 3;
        const hasSku = sku.trim().length >= 2;
        const hasBarcode = barcode.trim().length >= 3;

        if (!tenantId || (!hasName && !hasSku && !hasBarcode)) {
            setResult(null);
            return;
        }

        const timer = setTimeout(async () => {
            setIsChecking(true);
            try {
                const res = await checkDuplicateProduct(tenantId, {
                    name: hasName ? name.trim() : undefined,
                    sku: hasSku ? sku.trim() : undefined,
                    barcode: hasBarcode ? barcode.trim() : undefined,
                    excludeId,
                });
                setResult(res);
            } catch {
                // Silently ignore network hiccup on typeahead check
            } finally {
                setIsChecking(false);
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [tenantId, name, sku, barcode, excludeId]);

    if (!result) return null;

    const { isExactMatch, exactMatchField, exactProduct, similarProducts } = result;

    if (!isExactMatch && (!similarProducts || similarProducts.length === 0)) {
        return null;
    }

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="my-3 overflow-hidden rounded-xl border text-xs"
            >
                {/* 1. EXACT MATCH WARNING (CRITICAL / BLOCKING) */}
                {isExactMatch && exactProduct && (
                    <div className="p-3.5 bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-900 flex items-start gap-3">
                        <div className="p-1.5 rounded-lg bg-rose-100 dark:bg-rose-900/60 text-rose-700 dark:text-rose-300 flex-shrink-0 mt-0.5">
                            <AlertCircle className="w-4 h-4" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <p className="font-bold text-rose-900 dark:text-rose-200">
                                Duplicate {exactMatchField?.toUpperCase()} Conflict
                            </p>
                            <p className="text-rose-700 dark:text-rose-300">
                                An active product with this {exactMatchField} already exists:
                                <span className="font-semibold ml-1">"{exactProduct.name}"</span>
                                {exactProduct.sku && <span className="font-mono text-[11px] ml-1.5 opacity-80">(SKU: {exactProduct.sku})</span>}
                            </p>
                            <div className="pt-1 flex items-center gap-3">
                                <Link
                                    to={`/products/${exactProduct.id}`}
                                    target="_blank"
                                    className="inline-flex items-center gap-1 font-semibold text-rose-800 dark:text-rose-200 hover:underline"
                                >
                                    <span>View existing product</span>
                                    <ExternalLink className="w-3 h-3" />
                                </Link>
                                <span className="text-rose-400">·</span>
                                <span className="text-rose-600 dark:text-rose-400">
                                    Change the {exactMatchField} to prevent saving errors.
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* 2. FUZZY / SIMILAR PRODUCTS NOTICE (NON-BLOCKING) */}
                {!isExactMatch && similarProducts && similarProducts.length > 0 && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-amber-900 dark:text-amber-200">
                                <CopySlash className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                                <span className="font-semibold">
                                    Similar Item Found ({similarProducts.length})
                                </span>
                                <span className="text-amber-700 dark:text-amber-300 text-[11px]">
                                    — e.g., "{similarProducts[0].name}" ({Math.round(similarProducts[0].similarity * 100)}% match)
                                </span>
                            </div>

                            <button
                                type="button"
                                onClick={() => setIsExpanded(!isExpanded)}
                                className="flex items-center gap-1 text-[11px] font-semibold text-amber-800 dark:text-amber-300 hover:text-amber-950 transition-colors"
                            >
                                <span>{isExpanded ? 'Hide' : 'Review'}</span>
                                {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            </button>
                        </div>

                        {isExpanded && (
                            <div className="pt-2 border-t border-amber-200/60 dark:border-amber-900/60 space-y-1.5">
                                <p className="text-[11px] text-amber-800 dark:text-amber-300">
                                    Ensure this is not a duplicate. Existing products can be combined later via the Merge tool:
                                </p>
                                <div className="space-y-1 max-h-36 overflow-y-auto">
                                    {similarProducts.map((p) => (
                                        <div
                                            key={p.id}
                                            className="flex items-center justify-between px-2.5 py-1.5 bg-white/70 dark:bg-slate-900/70 rounded-lg text-[11px]"
                                        >
                                            <div className="truncate mr-2">
                                                <span className="font-medium text-slate-800 dark:text-slate-200">{p.name}</span>
                                                {p.sku && <span className="text-slate-400 font-mono ml-1.5">({p.sku})</span>}
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0 text-slate-500">
                                                <span className="text-[10px] font-medium text-amber-700 bg-amber-100/70 px-1.5 py-0.5 rounded">
                                                    {Math.round(p.similarity * 100)}%
                                                </span>
                                                <Link
                                                    to={`/products/${p.id}`}
                                                    target="_blank"
                                                    className="p-1 hover:text-emerald-600 transition-colors"
                                                    title="View in new tab"
                                                >
                                                    <ExternalLink className="w-3 h-3" />
                                                </Link>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </motion.div>
        </AnimatePresence>
    );
}
